import type { ParticipantCandidate } from "../request/request";

/**
 * Helpers for the request participants block of a Kontrol Edge ticket
 * (CTX-683). Kept out of the component so the label and error wording the
 * picker shows can be tested without rendering it.
 */

/** Dropdown label for a candidate; the name alone is not unique, the email is. */
export function getCandidateLabel(candidate: ParticipantCandidate): string {
  const name = candidate.name?.trim();
  if (!name || name.toLowerCase() === candidate.email.toLowerCase()) {
    return candidate.email;
  }
  return `${name} (${candidate.email})`;
}

/**
 * The selection after the user changed the picks in one customer's list.
 * Picks from the other customers stay as they were; this customer's are
 * replaced by `labels`, kept in the order the user picked them.
 */
export function mergeCandidateSelection(
  selected: ParticipantCandidate[],
  shownCandidates: ParticipantCandidate[],
  labels: string[]
): ParticipantCandidate[] {
  const shownEmails = new Set(shownCandidates.map((c) => c.email));
  const byLabel = new Map(
    shownCandidates.map((c) => [getCandidateLabel(c), c])
  );
  const fromElsewhere = selected.filter((c) => !shownEmails.has(c.email));
  const fromHere = labels
    .map((label) => byLabel.get(label))
    .filter((c): c is ParticipantCandidate => Boolean(c));
  return [...fromElsewhere, ...fromHere];
}

interface ParticipantsErrorBody {
  message?: string;
  data?: {
    notAllowed?: string[];
    couldNotRegister?: string[];
    couldNotAdd?: string[];
  } | null;
}

/**
 * Turns a failed participants call into one sentence for the user. A 422
 * names the people that were refused — the backend adds all of them or none,
 * so the user has to know which ones to drop before retrying.
 */
export function describeParticipantsError(error: unknown): string {
  const body = (error as { response?: { data?: ParticipantsErrorBody } })
    ?.response?.data;
  const notAllowed = body?.data?.notAllowed ?? [];
  const couldNotRegister = body?.data?.couldNotRegister ?? [];
  const couldNotAdd = body?.data?.couldNotAdd ?? [];

  const parts: string[] = [];
  if (notAllowed.length) {
    parts.push(`Not allowed on this ticket: ${notAllowed.join(", ")}.`);
  }
  if (couldNotRegister.length) {
    parts.push(
      `Couldn't create a support portal account for: ${couldNotRegister.join(", ")}.`
    );
  }
  if (couldNotAdd.length) {
    // Jira refused the add itself, often for an account created moments
    // ago; the backend already retried, so waiting a little usually works.
    parts.push(
      `Jira didn't accept these people as participants yet: ${couldNotAdd.join(", ")}. Try again in a minute.`
    );
  }
  if (parts.length) return `Nobody was added. ${parts.join(" ")}`;

  return body?.message || "Something went wrong. Please try again.";
}
